/**
 * RegistrationWizard Component
 *
 * Multi-step container managing the two-phase provider registration workflow.
 * Handles step progression, visual indicators, and workflow state management.
 *
 * @module components/provider-registration/RegistrationWizard
 */

import React, { useEffect } from 'react';
import { cn } from '@/utils/cn';
import { Card, CardBody } from '@/components/ui/Card';
import { StepIndicator, type StepConfig } from '@/components/ui/StepIndicator';
import { RegistrationForm } from './RegistrationForm';
import { ProfileUpdateForm } from './ProfileUpdateForm';
import { RegistrationSuccess } from './RegistrationSuccess';
import { useRegistrationFlow } from '@/hooks/useRegistrationFlow';
import { useTheme } from '@/hooks/useTheme';
import type { RegistrationStep } from '@/types/registration';

/**
 * RegistrationWizard component props interface
 */
export interface RegistrationWizardProps {
  /** Custom class name */
  className?: string;
  /** Callback when workflow completes */
  onComplete?: () => void;
  /** Callback when step changes */
  onStepChange?: (step: RegistrationStep) => void;
}

/**
 * Step configuration for the wizard
 */
const WIZARD_STEPS: StepConfig[] = [
  {
    key: 'registration',
    title: 'Create Account',
    description: 'Basic account information',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
      </svg>
    )
  },
  {
    key: 'profile_update',
    title: 'Professional Details',
    description: 'Provider information',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
      </svg>
    )
  },
  {
    key: 'complete',
    title: 'Complete',
    description: 'Registration successful',
    icon: (
      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
      </svg>
    )
  }
];

/**
 * RegistrationWizard component for managing the provider registration workflow
 *
 * @example
 * ```tsx
 * <RegistrationWizard
 *   autoLoadSavedData={true}
 *   onComplete={() => navigate('/dashboard')}
 *   onStepChange={(step) => analytics.track('registration_step', { step })}
 * />
 * ```
 */
export const RegistrationWizard: React.FC<RegistrationWizardProps> = ({
  className,
  onComplete,
  onStepChange
}) => {
  const { isDark } = useTheme();

  // Registration flow hook
  const {
    currentStep,
    workflow,
    registrationForm,
    profileForm,
    handlers
  } = useRegistrationFlow({
    validationDebounce: 300,
    showErrorsImmediately: false
  });

  // Notify parent of step changes
  useEffect(() => {
    if (currentStep) {
      onStepChange?.(currentStep);
    }
  }, [currentStep, onStepChange]);

  // Notify parent of completion
  useEffect(() => {
    if (currentStep === 'complete' && workflow.status === 'pending_approval') {
      onComplete?.();
    }
  }, [currentStep, workflow.status, onComplete]);

  // Show loading state while determining initial step
  if (currentStep === null) {
    return (
      <div className={cn('w-full', className)}>
        <div className="flex justify-center">
          <Card className="w-full max-w-2xl shadow-lg">
            <CardBody className="p-8">
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 mx-auto mb-4"></div>
                <p className={cn(
                  "text-sm",
                  isDark ? "text-neutral-400" : "text-neutral-600"
                )}>
                  Loading...
                </p>
              </div>
            </CardBody>
          </Card>
        </div>
      </div>
    );
  }


  /**
   * Render current step content
   */
  const renderStepContent = () => {
    switch (currentStep) {
      case 'registration':
        return (
          <RegistrationForm
            onSubmit={handlers.onRegistrationSubmit}
            isLoading={registrationForm.isSubmitting}
            initialData={registrationForm.values}
            submitError={registrationForm.submitError}
          />
        );

      case 'profile_update':
        return (
          <ProfileUpdateForm
            providerId={workflow.providerId || 'temp-provider-id'}
            onSubmit={handlers.onProfileSubmit}
            isLoading={profileForm.isSubmitting}
            initialData={profileForm.values}
            submitError={profileForm.submitError}
            mode='registration'
          />
        );

      case 'complete':
        return (
          <RegistrationSuccess />
        );

      default:
        return (
          <div className="text-center py-8">
            <p className={cn(
              isDark ? "text-neutral-400" : "text-neutral-600"
            )}>
              Unknown step: {currentStep}
            </p>
          </div>
        );
    }
  };

  return (
    <div className={cn('max-w-4xl mx-auto p-6', className)}>
      {/* Progress indicator */}
      <div className="mb-8">
        <StepIndicator
          steps={WIZARD_STEPS}
          currentStep={currentStep}
          ariaLabel="Registration progress"
          className="mb-4"
          variant="horizontal"
          size="md"
        />
      </div>

      {/* Step Content - Wrapped in Card */}
      <div className="flex justify-center">
        <Card className="w-full max-w-2xl shadow-lg">
          <CardBody className="p-8">
            {renderStepContent()}
          </CardBody>
        </Card>
      </div>
    </div>
  );
};

export default RegistrationWizard;